import * as THREE from "three";
import Experience from "../Experience.js";
import { gsap } from "gsap";
import settings from "../settings.js";
import { openPanel, getOpenPanel } from "../Utils/Panels.js";

export default class Room {
  constructor() {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.group = this.experience.group;
    this.sizes = this.experience.sizes;
    this.raycaster = this.experience.raycaster;
    this.resources = this.experience.resources;
    this.time = this.experience.time;
    this.debug = this.experience.debug;
    this.currentIntersect = [];
    this.resource = this.resources.items.roomModel;
    this.textures = this.resources.items.screenTexture;
    this.texturesHover = this.resources.items.screenTextureHover;
    this.hoveredObject = null;

    this.peachToggle = this.resources.items.peachToggle;
    this.socksToggle = this.resources.items.socksToggle;
    this.peachToggle.flipY = false;
    this.socksToggle.flipY = false;
    this.peachToggle.colorSpace = THREE.SRGBColorSpace;
    this.socksToggle.colorSpace = THREE.SRGBColorSpace;
    this.toggleEmissive = settings.room.catToggleEmissive
      ? this.resources.items.emissiveToggle
      : null;
    if (this.toggleEmissive) this.toggleEmissive.flipY = false;

    // Debug
    if (this.debug.active) {
      this.debugFolder = this.debug.ui.addFolder("room");
    }

    window.addEventListener("click", (event) => {
      // Ignore clicks on HTML (buttons, notices) that sit over the canvas
      if (event.target !== this.experience.canvas) return;
      if (!this.currentIntersect || this.isAnyDivVisible) return;
      if (this.experience.camera.flying) return;

      const object = this.currentIntersect.object;
      if (object === this.toggleCube) {
        this.toggleCat();
      } else {
        this.showTarget(object);
      }
    });

    if (settings.room.hoverEffects) {
      this.label = document.querySelector(".hover-label");
      window.addEventListener("mousemove", (event) => {
        this.label.style.transform = `translate(${event.clientX + 18}px, ${event.clientY + 18}px)`;
      });
    }

    this.setModel();
  }

  setModel() {
    this.model = this.resource.scene;
    this.group.addModel(this.model);

    this.toggleCube = new THREE.Mesh(
      new THREE.BoxGeometry(...settings.room.toggleHitbox.size),
      new THREE.MeshBasicMaterial({
        color: 0x00f00,
        transparent: true,
        opacity: 0,
      }),
    );
    this.toggleCube.position.set(...settings.room.toggleHitbox.position);
    this.group.addModel(this.toggleCube);
    this.lampMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: settings.room.lampShade.opacity,
      emissive: 0xffaa33,
      emissiveIntensity: settings.room.lampShade.emissiveIntensity,
    });
    this.screenMaterial = new THREE.MeshStandardMaterial();
    this.screenMaterial.map = this.textures;
    this.screenMaterial.emissive = new THREE.Color(0xffe9d4);
    this.screenMaterial.emissiveMap = this.textures;
    this.screenMaterial.emissiveIntensity = 0;
    this.screenMaterial.color = new THREE.Color(0x000000);

    this.textures.flipY = false;
    this.textures.colorSpace = THREE.SRGBColorSpace;
    this.texturesHover.flipY = false;
    this.texturesHover.colorSpace = THREE.SRGBColorSpace;
    this.model.traverse((child) => {
      if (child.name === "Room_1") {
        this.wall = child; // the back wall; the camera glide stays inside it
        child.receiveShadow = settings.room.receiveShadow;
      } else if (child.name === "HourHand_1") {
        this.hourHand = child;
      } else if (child.name === "MinuteHand_1") {
        this.minuteHand = child;
      } else if (child.name === "Picture1_1") {
        this.picture1 = child;
      } else if (child.name === "Picture2_1") {
        this.picture2 = child;
      } else if (child.name === "Picture3_1") {
        this.picture3 = child;
      } else if (child.name === "Envelopes_1") {
        this.envelope = child;
        this.envelope.position.y += settings.room.envelopeOffsetY;
        // Its own copy of the shared "Walls" material, so only the envelope's
        // outline pulses (and scroll lighting leaves it alone)
        this.envelope.material = this.envelope.material.clone();
        this.envelope.material.name = "Envelope";
      } else if (child.name === "Sheets_1") {
        this.sheets = child;
      } else if (child.name === "LeftScreen_1") {
        child.material = this.screenMaterial;
        this.leftScreen = child;
      } else if (child.name === "RightScreen_1") {
        // Desktop model only
        child.material = this.screenMaterial;
        this.rightScreen = child;
      } else if (child.name === "CatToggle_1") {
        this.catToggle = child;
        this.catToggle.material.map = this.peachToggle;
        if (this.toggleEmissive) {
          this.catToggle.material.emissiveMap = this.toggleEmissive;
          this.catToggle.material.emissiveIntensity = 1;
        }
      } else if (child.name === "LampShade_1") {
        this.lampShade = child;
        this.lampShade.material = this.lampMaterial;
      }
    });

    // Shared by the picture icons and the cat toggle
    this.picture1.material.emissiveIntensity = settings.room.pictureGlow;

    // Clickable objects: the panel each one opens and its hover label
    this.targets = new Map([
      [this.picture1, { panel: "picture1-div", label: "About me" }],
      [this.picture2, { panel: "picture2-div", label: "Experience" }],
      [this.picture3, { panel: "picture3-div", label: "Education" }],
      [this.sheets, { panel: "contact-div", label: "Contact" }],
      [this.leftScreen, { panel: "project-div", label: "Projects" }],
      [this.toggleCube, { label: "Switch cat" }],
    ]);
    this.objectsToTest = [...this.targets.keys()];

    if (this.debug.active) {
      this.debugFolder
        .add(this.toggleCube.position, "x")
        .name("toggleCubeX")
        .min(-5)
        .max(5)
        .step(0.0001);

      this.debugFolder
        .add(this.toggleCube.position, "y")
        .name("toggleCubeY")
        .min(-5)
        .max(5)
        .step(0.0001);

      this.debugFolder
        .add(this.toggleCube.position, "z")
        .name("toggleCubeZ")
        .min(-5)
        .max(5)
        .step(0.0001);
    }
  }

  // Glide the camera towards a clickable object, then open its panel
  showTarget(object) {
    const panel = document.getElementById(this.targets.get(object).panel);
    this.experience.camera.flyTo(object, () => openPanel(panel));
  }

  // Same, for the nav menu, which knows panels by id
  showPanel(panelId) {
    for (const [object, target] of this.targets) {
      if (target.panel === panelId) return this.showTarget(object);
    }
    openPanel(document.getElementById(panelId));
  }

  toggleCat() {
    const cat = this.experience.world.cat;
    cat.updateTexture();

    // Follow the cat's state rather than the switch position, which may be mid-tween
    const isSocks = !cat.isPeach;
    this.catToggle.material.map = isSocks ? this.socksToggle : this.peachToggle;
    gsap.to(this.catToggle.position, {
      z: isSocks ? settings.room.catToggleSlideZ : 0,
      duration: 0.3,
      ease: "power1.inOut",
    });
  }

  update() {
    const date = new Date();
    const minute = date.getMinutes();
    const hour = date.getHours() % 12;
    const minuteRadians = (Math.PI * 2) / 60;
    const hourRadians = (Math.PI * 2) / 12;

    this.minuteHand.rotation.x = -minute * minuteRadians;

    this.hourHand.rotation.x = -(
      hour * hourRadians +
      (minute / 60) * hourRadians
    );

    const pulse = settings.room.envelopePulse;
    if (pulse) {
      const wave = 0.5 + 0.5 * Math.sin(this.time.elapsedTime * pulse.speed);
      this.envelope.material.emissiveIntensity =
        pulse.min + (pulse.max - pulse.min) * wave;
    }

    if (settings.room.wiggle) {
      const t = this.time.elapsedTime;
      this.picture1.rotation.x = Math.sin(t * 5) * 0.05;
      this.picture2.rotation.x = Math.sin(t * 4) * 0.05;
      this.picture3.rotation.x = Math.sin(t * 6) * 0.05;
      this.envelope.rotation.x = Math.sin(t * 6) * 0.05;
    }

    this.isAnyDivVisible = !!getOpenPanel();

    this.intersects = this.raycaster.instance.intersectObjects(
      this.objectsToTest,
    );
    this.currentIntersect = this.intersects.length ? this.intersects[0] : null;

    if (!settings.room.hoverEffects) return;

    this.setHovered(
      this.currentIntersect &&
        !this.isAnyDivVisible &&
        !this.experience.camera.flying
        ? this.currentIntersect.object
        : null,
    );
  }

  // Only tween when the hovered object changes, not every frame
  setHovered(object) {
    if (object === this.hoveredObject) return;

    if (this.hoveredObject) this.unhover(this.hoveredObject);
    if (object) this.hover(object);

    this.hoveredObject = object;
    document.body.style.cursor = object ? "pointer" : "default";
    if (object) this.label.textContent = this.targets.get(object).label;
    this.label.classList.toggle("visible", !!object);
  }

  hover(object) {
    if (object === this.sheets) {
      gsap.to(this.envelope.position, {
        y: 0.2,
        duration: 0.1,
        ease: "power1.inOut",
      });
      gsap.to(this.envelope.scale, {
        x: 1.1,
        y: 1.1,
        z: 1.1,
        duration: 0.1,
        ease: "power1.inOut",
      });
    } else if (object === this.leftScreen) {
      this.screenMaterial.map = this.texturesHover;
    } else {
      gsap.to(object.scale, {
        x: 1.03,
        y: 1.03,
        z: 1.03,
        duration: 0.5,
      });
    }
  }

  unhover(object) {
    if (object === this.sheets) {
      gsap.to(this.envelope.position, {
        y: 0,
        duration: 0.2,
        ease: "power1.inOut",
      });
      gsap.to(this.envelope.scale, {
        x: 1,
        y: 1,
        z: 1,
        duration: 0.1,
        ease: "power1.inOut",
      });
    } else if (object === this.leftScreen) {
      this.screenMaterial.map = this.textures;
    } else {
      gsap.to(object.scale, {
        x: 1,
        y: 1,
        z: 1,
        duration: 0.5,
      });
    }
  }
}
